/**
 * Parses ECharts JSON configuration from ERAX response content.
 *
 * ERAX embeds chart specifications using delimiters:
 *   :::ECHARTS_JSON_START:::{...JSON...}:::ECHARTS_JSON_END:::
 *
 * This module extracts the ECharts option object and returns the
 * remaining content (with the ECharts block stripped) for markdown rendering.
 */

const ECHARTS_PATTERN =
  /:::ECHARTS_JSON_START:::([\s\S]*?):::ECHARTS_JSON_END:::/g;

export interface ParsedEchartsResult {
  /** Parsed ECharts option objects (one per block found). */
  charts: Record<string, unknown>[];
  /** The response content with all ECharts blocks removed (for markdown rendering). */
  cleanContent: string;
}

/**
 * Extract all `:::ECHARTS_JSON_START:::...:::ECHARTS_JSON_END:::` blocks
 * from the response text.
 *
 * @returns Parsed charts and the cleaned content, or `null` if no blocks found.
 */
export function parseEchartsJson(content: string): ParsedEchartsResult | null {
  if (!content || typeof content !== 'string') return null;

  const charts: Record<string, unknown>[] = [];
  // Create a fresh regex each call to avoid shared lastIndex state
  const re = new RegExp(ECHARTS_PATTERN.source, ECHARTS_PATTERN.flags);
  let match: RegExpExecArray | null;

  while ((match = re.exec(content)) !== null) {
    const raw = match[1].trim();
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        charts.push(parsed);
      }
    } catch {
      // Malformed JSON — skip this block
      console.warn('[parseEchartsJson] Failed to parse ECharts JSON block:', raw.slice(0, 100));
    }
  }

  if (charts.length === 0) return null;

  // Strip all ECharts blocks from the content
  const cleanContent = content
    .replace(ECHARTS_PATTERN, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  return { charts, cleanContent };
}
